import { Injectable, NgZone, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService, TranslationObject } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';

export type Language = 'sr-Cyrl' | 'sr-Latn';

export const LANGUAGES: { code: Language; label: string }[] = [
  { code: 'sr-Latn', label: 'Srpski' },
  { code: 'sr-Cyrl', label: 'Српски' },
];

const STORAGE_KEY = 'language';
const DEFAULT_LANG: Language = 'sr-Latn';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translate = inject(TranslateService);
  private readonly ngZone = inject(NgZone);
  private readonly http = inject(HttpClient);

  readonly current = signal<Language>(this.resolveInitialLang());

  constructor() {
    this.ngZone.run(() => {
      forkJoin(
        LANGUAGES.map(({ code }) =>
          this.http.get<TranslationObject>(`/i18n/${code}.json`).pipe(
            tap(t => this.translate.setTranslation(code, t)),
            catchError(() => of(null)),
          ),
        ),
      )
        .pipe(take(1))
        .subscribe(() => {
          this.translate.use(this.current()).pipe(take(1)).subscribe();
        });
    });
  }

  setLanguage(lang: Language): void {
    this.current.set(lang);
    this.ngZone.run(() => {
      this.translate.use(lang).pipe(take(1)).subscribe();
    });
    localStorage.setItem(STORAGE_KEY, lang);
  }

  private resolveInitialLang(): Language {
    return (localStorage.getItem(STORAGE_KEY) as Language) ?? DEFAULT_LANG;
  }
}
